// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import './entry.js';

import * as Sentry from '@sentry/react';
import App from 'components/app';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { initBrowserSentry, setFrontendContext } from 'fe-shared-lib/src/observability';
import { SentryServiceTag } from 'networked-shared-interfaces/constants/observability-service-tag';
import { UserUtility } from 'src/helpers/userUtility';

console.log("Locize -> Before import");
// Added Language
import i18n from "./i18n.js";
console.log("Locize -> After import", i18n);

/** Do not send events to Sentry when using webpack-dev-server on this machine (make dev / make qa / make prod). */
function isBrowserLocalhost() {
	if (typeof window === 'undefined') {
		return false;
	}
	const { hostname } = window.location;
	return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

const sentryEnabled = !isBrowserLocalhost();

const apiHost = (process.env.REACT_APP_BASE_URL || '').replace(/^https?:\/\//, '');

// White-label deployments proxy to https://be.<tenant-domain> at runtime.
// Match that dynamically so sentry-trace / baggage headers are injected for
// those outgoing requests too.
const whiteLabelApiRegex = /^https:\/\/be\./;

// tracePropagationTargets tells Sentry's browser tracing which outgoing
// requests should receive the sentry-trace + baggage headers so the backend
// can join the same distributed trace.
//
// BACKEND REQUIREMENTS (other repo):
//   1. Enable performance tracing and call Sentry.continueTrace({ sentryTrace, baggage })
//      at the start of each request handler using the incoming header values.
//   2. CORS: allow the sentry-trace and baggage request headers in the Access-Control-Allow-Headers
//      preflight response so the browser does not block the request:
//        Access-Control-Allow-Headers: ..., sentry-trace, baggage
//   3. DSN / org alignment is not required for trace linkage (it is header-based), but
//      sharing the same Sentry org lets you navigate between frontend issues and backend traces.
if (sentryEnabled) {
	initBrowserSentry(Sentry, {
		dsn: process.env.REACT_APP_SENTRY_DSN || '',
		environment: process.env.REACT_APP_ENV || 'dev',
		serviceTag: SentryServiceTag.ReactFrontend,
		// release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
		tracePropagationTargets: [
			'localhost',
			...(apiHost ? [apiHost] : []),
			whiteLabelApiRegex,
		],
	});

	// Hydrate Sentry user + community tags from storage on cold load
	const storedUser = UserUtility.getUserDetails();
	const storedCommunity = UserUtility.getSelectedCommunityId();
	if (storedUser?.userId) {
		setFrontendContext({
			user: { id: storedUser.userId, email: storedUser.email, name: storedUser.name },
			communityId: storedCommunity || undefined,
			communityName: UserUtility.getSelectedCommunityName(),
		});
	}
}

function renderRootComponent() {
	const rootElement = document.getElementById('root');
	if (!rootElement) {
		return;
	}
	const root = createRoot(rootElement);
	root.render(
		sentryEnabled ? (
			<Sentry.ErrorBoundary>
				<App />
			</Sentry.ErrorBoundary>
		) : (
			<App />
		),
	);
}

/**
 * Adds a function to be invoked onload appended to any existing onload
 * event handlers.
 *
 * @param   {function} fn onload event handler
 *
 */
function appendOnLoadEvent(fn) {
	if (window.attachEvent) {
		window.attachEvent('onload', fn);
	} else if (window.onload) {
		const curronload = window.onload;
		window.onload = (evt) => {
			curronload(evt);
			fn(evt);

		};
	} else {
		window.onload = fn;
	}
}

appendOnLoadEvent(() => {
	// Do the pre-render setup and call renderRootComponent when done
	renderRootComponent();
});


// Global listener for ChunkLoadError
window.addEventListener('error', (e) => {
	console.log("Global listener for ChunkLoadError");

	const error = e;
	if (error?.message && error?.message?.includes('chunk')) {
		console.warn('ChunkLoadError detected, reloading...');
		window.location.reload();
	}
});

// Optional: also catch unhandled promise rejections
window.addEventListener('unhandledrejection', (e) => {
	console.log("also catch unhandled promise rejections");

	if (
		e.reason &&
		e.reason.message &&
		e.reason.message.includes('chunk')
	) {
		console.warn('ChunkLoadError (Promise rejection) detected, reloading...');
		window.location.reload();
	}
});